import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from '../../../api/axiosInstance';

/**
 * Export products to Excel file
 * Note: The exported Excel file will NOT include ID column.
 * Products are identified by SKU only.
 * @param {Object} params - Export parameters
 * @param {Array} params.ids - Optional array of product IDs to export (used for filtering only, not included in Excel)
 * @param {String} params.locale - Optional locale for translations (default: "en")
 */
export const exportProductsFunc = createAsyncThunk(
  "exportProductsSlicer/exportProductsFunc",
  async (params = {}, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const response = await api.post(
        'dashboard/products/export',
        params,
        {
          responseType: 'blob', // Important for file downloads
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'products-export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Decode URI if needed
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // Keep original if decode fails
          }
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Download export template
 * @param {String} locale - Optional locale for translations (default: "en")
 */
export const exportTemplateFunc = createAsyncThunk(
  "exportProductsSlicer/exportTemplateFunc",
  async (locale = "en", thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const response = await api.get(
        `dashboard/products/export-template?locale=${locale}`,
        {
          responseType: 'blob',
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'products-template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // Keep original if decode fails
          }
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const exportProductsSlicer = createSlice({
  name: "exportProductsSlicer",
  initialState: {
    loading: false,
    templateLoading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetExportState: (state) => {
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Export Products
    builder
      .addCase(exportProductsFunc.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(exportProductsFunc.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(exportProductsFunc.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // Export Template
    builder
      .addCase(exportTemplateFunc.pending, (state) => {
        state.templateLoading = true;
        state.error = null;
      })
      .addCase(exportTemplateFunc.fulfilled, (state) => {
        state.templateLoading = false;
      })
      .addCase(exportTemplateFunc.rejected, (state, action) => {
        state.templateLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetExportState } = exportProductsSlicer.actions;
export default exportProductsSlicer.reducer;
